import { useEffect, useRef, useState, type FocusEvent } from "react";
import type { GraphNode } from "../types";
import { Icon } from "./Icon";

interface AccessibleNodeListProps {
  nodes: GraphNode[];
  open: boolean;
  selectedNodeId: string | null;
  typeLabels: Map<string, string>;
  onClose: () => void;
  onSelect: (nodeId: string) => void;
}

export function AccessibleNodeList({
  nodes,
  open,
  selectedNodeId,
  typeLabels,
  onClose,
  onSelect,
}: AccessibleNodeListProps) {
  const panelRef = useRef<HTMLElement>(null);
  const keyboardInteractionRef = useRef(false);
  const [keyboardFocusActive, setKeyboardFocusActive] = useState(false);

  useEffect(() => {
    const handleKeyDown = () => {
      keyboardInteractionRef.current = true;
      if (panelRef.current?.contains(document.activeElement)) {
        setKeyboardFocusActive(true);
      }
    };
    const handlePointerDown = () => {
      keyboardInteractionRef.current = false;
      setKeyboardFocusActive(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("pointerdown", handlePointerDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("pointerdown", handlePointerDown);
    };
  }, []);

  useEffect(() => {
    if (open) panelRef.current?.focus();
    else setKeyboardFocusActive(false);
  }, [open]);

  const handleFocusCapture = () => {
    if (keyboardInteractionRef.current) setKeyboardFocusActive(true);
  };

  const handleBlurCapture = (event: FocusEvent<HTMLElement>) => {
    const nextTarget = event.relatedTarget as Node | null;
    if (!nextTarget || !event.currentTarget.contains(nextTarget)) setKeyboardFocusActive(false);
  };

  if (!open) return null;

  return (
    <section
      aria-label="키보드로 탐색 가능한 노드 목록"
      className={`node-list-panel${keyboardFocusActive ? " is-keyboard-focus" : ""}`}
      onBlurCapture={handleBlurCapture}
      onFocusCapture={handleFocusCapture}
      onKeyDown={(event) => {
        if (event.key === "Escape") onClose();
      }}
      ref={panelRef}
      tabIndex={-1}
    >
      <header>
        <div>
          <span>노드 목록</span>
          <strong>{nodes.length}</strong>
        </div>
        <button aria-label="노드 목록 닫기" className="icon-button" onClick={onClose} type="button">
          <Icon name="close" size={17} />
        </button>
      </header>
      <p className="node-list-help">
        <span className="node-list-help-touch">항목을 눌러 노드를 선택하세요.</span>
        <span className="node-list-help-keyboard">Tab 키로 이동하고 Enter 키로 노드를 선택할 수 있습니다.</span>
      </p>
      <ul className="node-list-scroll">
        {nodes.length > 0 ? (
          nodes.map((node) => (
            <li key={node.id}>
              <button
                aria-current={node.id === selectedNodeId ? "true" : undefined}
                className={node.id === selectedNodeId ? "is-selected" : ""}
                onClick={() => onSelect(node.id)}
                type="button"
              >
                <span className="node-list-mark" aria-hidden="true" />
                <span>
                  <strong>{node.title}</strong>
                  <small>{typeLabels.get(node.type) ?? node.type}</small>
                </span>
                <Icon name="arrow-right" size={15} />
              </button>
            </li>
          ))
        ) : (
          <li className="node-list-empty">현재 필터와 일치하는 노드가 없습니다.</li>
        )}
      </ul>
    </section>
  );
}
